import { useQueryClient } from '@tanstack/react-query';
import { useApiMutation } from '@/api/hooks/useMutation';
import { profilePictureKeys } from '@/api/queryKeys';
import type { UpdateMemberPictureResponse } from '@bvengineering/cas-member-gateway-service-client';

/**
 * Hook to upload a new profile picture.
 * This mirrors the mobile app's useUploadProfilePicture hook.
 *
 * Uses the Api container's axios instance directly for file upload
 * because the generated OpenAPI client doesn't properly handle
 * multipart/form-data file uploads.
 *
 * @example
 * const { mutate: upload } = useUploadProfilePicture();
 * upload(file);
 */
export function useUploadProfilePicture() {
  const queryClient = useQueryClient();

  return useApiMutation<UpdateMemberPictureResponse, Error, File>({
    mutationFn: (api) => async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);

      const url = `${api.basePath}/members/me/picture`;

      const response = await api.axios.post<UpdateMemberPictureResponse>(url, formData, {
        timeout: 60000,
        headers: {
          // Let axios set Content-Type with boundary for multipart
          'Content-Type': 'multipart/form-data',
        },
      });

      return response.data;
    },
    onSuccess: () => {
      // Invalidate profile picture queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: profilePictureKeys.all,
      });
    },
  });
}
